<template>
  <div class="process-materials-by-category">
    <div
      v-for="categoria in categoriasDelProceso"
      :key="`${proceso._uid}-cat-${categoria.CategoriaMaterial.Codigo}`"
      class="category-materials-group collapsible-category"
    >
      <!-- Header de la categoría siempre visible -->
      <div class="category-materials-header">
        <div
          class="category-name-collapsible"
          @click="toggleCategoriaCollapse(categoria.CategoriaMaterial.Codigo)"
        >
          <el-icon
            class="collapse-icon"
            :class="{ expanded: !isCategoriaColapsada(categoria.CategoriaMaterial.Codigo) }"
          >
            <ArrowDown />
          </el-icon>
          <div class="category-name">
            <span class="category-name-text">{{ categoria.CategoriaMaterial.Descripcion }}</span>
            <span
              v-if="categoria.MultiMaterial"
              class="multi-material-indicator"
              >Multi-Item</span
            >
          </div>
        </div>

        <el-badge
          :value="getMaterialesDeCategoria(categoria.CategoriaMaterial.Codigo).length"
          class="materials-count-badge"
        >
        </el-badge>

        <el-button
          size="small"
          :disabled="
            !categoria.MultiMaterial &&
            getMaterialesDeCategoria(categoria.CategoriaMaterial.Codigo).length >= 1
          "
          @click="addMaterialToCategory(categoria.CategoriaMaterial.Codigo)"
        >
          + item
        </el-button>
      </div>

      <!-- Contenido colapsable - lista de materiales -->
      <transition name="collapse">
        <div
          v-show="!isCategoriaColapsada(getCategoriaUid(categoria.CategoriaMaterial.Codigo))"
          class="category-materials-list"
        >
          <el-row
            v-for="(material, matIndex) in getMaterialesDeCategoria(
              categoria.CategoriaMaterial.Codigo,
            )"
            :key="material._uid"
            :gutter="8"
            class="material-row"
          >
            <el-col :span="20">
              <CustomSelect
                :model-value="getMaterialSeleccionado(material, categoria.CategoriaMaterial.Codigo)"
                :span-column="24"
                :field-key="'value'"
                :field-label="'label'"
                :label="'Material'"
                :settings="{
                  filterable: true,
                  clearable: true,
                  options: getMaterialesDisponibles(categoria.CategoriaMaterial.Codigo),
                  placeholder: 'Seleccionar material',
                }"
                :store-object="true"
                @update:model-value="
                  onMaterialSelectChanged(categoria.CategoriaMaterial.Codigo, matIndex, $event)
                "
              />
            </el-col>
            <el-col
              :span="4"
              class="material-action-col"
            >
              <el-button
                circle
                size="small"
                type="danger"
                :icon="IconDelete"
                title="Eliminar material"
                @click="removeMaterialFromCategory(categoria.CategoriaMaterial.Codigo, matIndex)"
              />
            </el-col>
          </el-row>

          <div
            v-if="getMaterialesDeCategoria(categoria.CategoriaMaterial.Codigo).length === 0"
            class="no-materials"
          >
            No hay items agregados para esta categoría
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
} from 'vue';
import {
  Delete as IconDelete,
  ArrowDown,
} from '@element-plus/icons-vue';
import CustomSelect from '@/shared/components/custom_select.vue';

// Props
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  proceso: {
    type: Object,
    required: true,
  },
  categoriasDelProceso: {
    type: Array,
    default: () => [],
  },
  materialesDisponibles: {
    type: Map,
    default: () => new Map(),
  },
});

// Emits
const emit = defineEmits(['update:modelValue', 'material-changed']);

// Estado local
const categoriasColapsadas = ref(new Set());

// Computed
const materialesPorCategoria = computed({
  get: () => props.modelValue || {},
  set: (value) => emit('update:modelValue', value),
});

// Función para generar IDs únicos
const uid = () => Math.random().toString(36).slice(2, 9);

// Métodos utilitarios
const getCategoriaUid = (codigoCategoria) => {
  return `${props.proceso._uid}-${codigoCategoria}`;
};

const getMaterialesDeCategoria = (codigoCategoria) => {
  if (!materialesPorCategoria.value[codigoCategoria]) {
    materialesPorCategoria.value[codigoCategoria] = [];
  }
  return materialesPorCategoria.value[codigoCategoria];
};

const getMaterialesDisponibles = (codigoCategoria) => {
  return props.materialesDisponibles.get(codigoCategoria) || [];
};

const getMaterialSeleccionado = (material, codigoCategoria) => {
  if (!material.materialId) return null;

  // Buscar el objeto completo del material basado en el ID almacenado
  const materialesDisponibles = getMaterialesDisponibles(codigoCategoria);
  return materialesDisponibles.find((mat) => mat.value === material.materialId) || null;
};

// Gestión de materiales
const addMaterialToCategory = (codigoCategoria) => {
  const categoria = props.categoriasDelProceso.find(
    (c) => c.CategoriaMaterial.Codigo === codigoCategoria,
  );

  if (!categoria?.MultiMaterial && getMaterialesDeCategoria(codigoCategoria).length >= 1) {
    return;
  }

  // Expandir la categoría automáticamente al agregar material
  categoriasColapsadas.value.delete(codigoCategoria);

  // Agregar nuevo material vacío
  const materialesCategoria = getMaterialesDeCategoria(codigoCategoria);
  materialesCategoria.push({
    materialId: null,
    _uid: uid(),
  });

  // Emitir cambios usando update:modelValue para centralización
  emit('update:modelValue', { ...materialesPorCategoria.value });
  emit('material-changed', { categoria: codigoCategoria, action: 'add' });
};

const removeMaterialFromCategory = (codigoCategoria, matIndex) => {
  const materialesCategoria = getMaterialesDeCategoria(codigoCategoria);
  materialesCategoria.splice(matIndex, 1);

  // Emitir cambios usando update:modelValue para centralización
  emit('update:modelValue', { ...materialesPorCategoria.value });
  emit('material-changed', { categoria: codigoCategoria, action: 'remove', index: matIndex });
};

const updateMaterialInCategory = (codigoCategoria, matIndex, nuevoMaterialId) => {
  const materialesCategoria = getMaterialesDeCategoria(codigoCategoria);
  if (materialesCategoria[matIndex]) {
    materialesCategoria[matIndex].materialId = nuevoMaterialId;
  }

  // Emitir cambios usando update:modelValue para centralización
  emit('update:modelValue', { ...materialesPorCategoria.value });
  emit('material-changed', {
    categoria: codigoCategoria,
    action: 'update',
    index: matIndex,
    materialId: nuevoMaterialId,
  });
};

const onMaterialSelectChanged = (codigoCategoria, matIndex, materialSeleccionado) => {
  // Actualizar el ID del material desde el objeto seleccionado
  const nuevoMaterialId =
    materialSeleccionado && materialSeleccionado.value ? materialSeleccionado.value : null;
  updateMaterialInCategory(codigoCategoria, matIndex, nuevoMaterialId);
};

// Gestión de collapse
const toggleCategoriaCollapse = (codigoCategoria) => {
  if (categoriasColapsadas.value.has(codigoCategoria)) {
    categoriasColapsadas.value.delete(codigoCategoria);
  } else {
    categoriasColapsadas.value.add(codigoCategoria);
  }
};

const isCategoriaColapsada = (codigoCategoria) => {
  return categoriasColapsadas.value.has(codigoCategoria);
};

// // Exponer métodos
// defineExpose({
//   inicializarEstadoColapso: () => {
//     props.categoriasDelProceso.forEach(categoria => {
//       const categoriaUid = getCategoriaUid(categoria.codigo)
//       categoriasColapsadas.value.add(categoriaUid)
//     })
//   }
// })
</script>

<style scoped>
.process-materials-by-category {
  width: 100%;
}

.category-materials-group {
  margin-bottom: 20px;
  background: #ffffff;
  border: 1px solid var(--el-border-color-light);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.collapsible-category {
  overflow: hidden;
}

.category-materials-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 0;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid var(--el-border-color-lighter);
  position: relative;
}

.category-name-collapsible {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  flex: 1;
  font-size: 14px;
  user-select: none;
}

.category-name-collapsible:hover {
  color: var(--el-color-primary);
}

.category-name-collapsible:hover .category-name-text {
  color: var(--el-color-primary);
}

.category-name {
  font-weight: 600;
  color: var(--el-text-color-primary);
  flex: 1;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-name-text {
  font-weight: 600;
  font-size: 14px;
  color: var(--el-text-color-primary);
}

.multi-material-indicator {
  background: linear-gradient(135deg, var(--el-color-warning) 0%, #e6a23c 100%);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.category-name-collapsible .collapse-icon {
  transition: transform 0.3s ease;
  transform: rotate(-90deg);
  color: var(--el-text-color-secondary);
  margin-right: 4px;
}

.category-name-collapsible .collapse-icon.expanded {
  transform: rotate(0deg);
}

.materials-count-badge {
  margin-left: auto;
  margin-right: 8px;
}

.materials-count-badge :deep(.el-badge__content) {
  background-color: var(--el-color-primary);
  font-size: 11px;
  font-weight: 600;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
}

.category-materials-header::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--el-color-primary);
}

.category-materials-header .el-button {
  background: white;
  border: 1px solid var(--el-color-primary);
  color: var(--el-color-primary);
  font-weight: 500;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.category-materials-header .el-button:hover {
  background: var(--el-color-primary);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(64, 158, 255, 0.3);
}

.category-materials-list {
  width: 100%;
  padding: 12px 16px 16px;
  background: #ffffff;
  border-top: 1px solid var(--el-border-color-lighter);
}

.material-row {
  display: flex;
  width: 100%;
  margin-bottom: 8px;
  padding: 8px;
  background: #fafbfc;
  border: 1px solid var(--el-border-color-extralight);
  border-radius: 6px;
  transition: all 0.2s ease;
  align-items: center;
}

.material-row:hover {
  background: #f0f2f5;
  border-color: var(--el-border-color-light);
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.material-row:last-child {
  margin-bottom: 0;
}

.material-action-col {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0;
}

.material-action-col .el-button {
  margin: 0;
}

.no-materials {
  color: var(--el-text-color-placeholder);
  font-style: italic;
  padding: 20px 0;
  text-align: center;
  background: #fafbfc;
  border-radius: 6px;
  border: 1px dashed var(--el-border-color-light);
}

/* Transición para el collapse */
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.collapse-enter-to,
.collapse-leave-from {
  max-height: 1000px;
  opacity: 1;
  transform: translateY(0);
}
</style>
